import { makeAutoObservable, reaction } from 'mobx';
import { RootStore } from './rootStore';
import { Rate, Room, getRooms } from '../apiMethods/getRooms';
import { addDays, differenceInCalendarDays, format, startOfDay } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import { isEqual } from 'lodash';
import { validatePromocode } from '../apiMethods/validatePromocode';
import { submitBooking } from '../apiMethods/submitBooking';
import { ServiceStore } from './serviceStore';
import { nanoid } from 'nanoid';

export type RateMode = 'CORP' | 'SPEC' | 'OPEN';

export type GuestRoom = {
  id: string;
  params: {
    adults: number;
    childs: {
      id: number;
      count: number;
      code: string;
      use_in_calc: boolean;
    }[];
  };
};

type ReservationParams = {
  first_name: string;
  last_name: string;
  middle_name?: string;
  phone: string;
  email: string;
  city?: string;
  comment: string;
  agreed: boolean;
};

const today = startOfDay(new Date());

export class ParamsStore {
  root: RootStore;
  serviceStore: ServiceStore;
  roomList: ListWithCategories<Room>;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);

    this.root = rootStore;
    this.serviceStore = new ServiceStore();

    this.roomList = new ListWithCategories('Все номера');
  }

  disposers: (() => void)[] = [];

  dispose() {
    this.disposers.forEach(f => f());
  }

  init() {
    this.disposers = [
      reaction(
        () => Boolean(this.root.authStore.isCompanyEmployee),
        () => {
          if (this.root.authStore.isCompanyEmployee) {
            this.setRateMode('CORP');
          } else {
            this.setRateMode('OPEN');
          }
        },
      ),
    ];
  }

  range:
    | {
        from: Date | undefined;
        to?: Date | undefined;
      }
    | undefined;

  initRange() {
    this.range = { from: today, to: addDays(today, 1) };
  }

  setRange(newRange?: { from: Date | undefined; to?: Date | undefined }) {
    this.range = newRange;
  }

  get from() {
    return this.appliedParams?.range?.from;
  }
  get to() {
    return this.appliedParams?.range?.to;
  }

  get nightsCount() {
    if (!this.to || !this.from) {
      return 0;
    }

    return differenceInCalendarDays(this.to, this.from);
  }

  promocode?: string;

  setPromocode(newPromocode?: string) {
    this.promocode = newPromocode;
  }

  promocodeValidationState = { success: false, failed: false, pending: false };
  setPromocodeValidationState(state: Partial<typeof this.promocodeValidationState>) {
    this.promocodeValidationState = { ...this.promocodeValidationState, ...state };
  }

  async validatePromocode(promocode: string) {
    const hotelId = this.root.configStore.hotelId;
    if (!hotelId) {
      return;
    }
    this.setPromocodeValidationState({ failed: false, success: false, pending: true });
    const { status } = (await validatePromocode(hotelId, promocode)) || {};
    if (status) {
      this.setPromocodeValidationState({ success: true, pending: false });
    } else {
      this.setPromocodeValidationState({ failed: true, pending: false });
    }
    return status;
  }

  rateMode?: RateMode = 'OPEN';

  setRateMode(rateMode: typeof this.rateMode) {
    this.rateMode = rateMode || 'OPEN';
  }

  initGuestRooms(
    childCategories: { id: number; code: string; count: number; use_in_calc: boolean }[],
  ) {
    const childs = childCategories.map(childCategory => ({
      id: childCategory.id,
      code: childCategory.code,
      count: 0,
      use_in_calc: childCategory.use_in_calc,
    }));

    const newRoom = {
      id: nanoid(),
      params: {
        adults: 2,
        childs,
      },
    };
    this.guestRooms = [newRoom];
  }

  guestRooms: GuestRoom[] = [];

  setGuestRooms = (guestRooms: typeof this.guestRooms) => {
    this.guestRooms = guestRooms;
  };

  countGuests(guestRooms?: GuestRoom[]) {
    if (!guestRooms?.length) {
      return [0, 0] as const;
    }
    const adults = guestRooms.reduce((sum, cur) => sum + cur.params.adults, 0);

    let childs = 0;
    for (const gr of guestRooms) {
      const count = gr.params.childs.reduce((sum, cur) => sum + cur.count, 0);
      childs += count;
    }
    return [adults, childs] as const;
  }

  get roomsCount() {
    return this?.appliedParams?.guestRooms.length;
  }

  get searchParams() {
    return {
      range: this.range,
      promocode: this.promocode,
      rateMode: this.rateMode,
      guestRooms: this.guestRooms,
    };
  }

  appliedSearchParams: null | {
    range: ParamsStore['range'];
    promocode: ParamsStore['promocode'];
    rateMode: ParamsStore['rateMode'];
    guestRooms: ParamsStore['guestRooms'];
  } = null;

  setAppliedParams(params: typeof this.appliedSearchParams) {
    this.appliedSearchParams = cloneDeep(params);
    this.serviceStore.resetServices();
  }

  get appliedParams() {
    return this.appliedSearchParams;
  }

  get hasUnappliedParams() {
    const { guestRooms, ...currentParams } = this.searchParams;

    const { guestRooms: appliedGuestRooms, ...appliedParams } = this.appliedSearchParams || {};
    const equal = isEqual(currentParams, appliedParams);

    // rooms compared separately since each room object has a unique id
    const areRoomsEqual = isEqual(
      guestRooms.flatMap(r => r.params),
      appliedGuestRooms?.flatMap(r => r.params),
    );

    return !(equal && areRoomsEqual);
  }

  resetAppliedParams() {
    const { range, promocode, rateMode, guestRooms } = this.appliedParams || {};

    this.setRange(range);
    this.setPromocode(promocode);
    this.setRateMode(rateMode);
    guestRooms && this.setGuestRooms(guestRooms);
  }

  async loadRooms(reservationId?: number) {
    this.setAppliedParams(this.searchParams);

    const hotelId = this.root.configStore.hotelId;
    if (!hotelId) {
      // TODO
      throw new Error('hotelId not found');
    }

    if (!this.range?.from || !this.range?.to) {
      return;
    }

    const bodyRequest = {
      arrival: format(this.range.from, 'yyyy-MM-dd'),
      departure: format(this.range.to, 'yyyy-MM-dd'),
      promo_code: this.promocode || '',
      reservation_id: reservationId,
      rooms: this.guestRooms.map(x => ({
        ...x.params,
        childs: x.params.childs.map(c => ({ ...c, code: 'CODE' })),
      })),
      rate_flow: this.rateMode || '',
    };

    this.roomList.setPending(true);
    const rooms = await getRooms(hotelId, bodyRequest);

    rooms && this.roomList.setItems(rooms);
    this.roomList.setPending(false);
  }

  getRoom(roomId: number) {
    const room = this.roomList.items.find(room => room.id === roomId);

    return room || null;
  }

  room: Room | null = null;

  selectRoom(roomId: number) {
    const room = this.getRoom(roomId);
    this.room = room || null;
  }

  rate: Rate | null = null;

  assertRateExists(rateId?: number) {
    const rate = this.room?.rates.find(rate => rate.id === rateId);

    if (!rate) {
      throw new Error('Не удалось найти выбранные ранее тариф. Бла-бла-бла');
    }
  }

  selectRate(rateId: number | null) {
    const rate = this.room?.rates.find(rate => rate.id === rateId);
    this.rate = rate || null;
  }

  get totalPrice() {
    if (!this.rate) {
      return null;
    }
    const servicesTotal = this.serviceStore.getTotalPrice();
    return this.rate?.total_price + servicesTotal;
  }

  guest?: {
    first_name: string;
    last_name: string;
    middle_name?: string;
    phone: string;
    email: string;
  };

  setGuest(guest: typeof this.guest) {
    this.guest = guest;
  }

  payment:
    | {
        id: number;
        payment_type: string;
        payment_alias: string;
        part: null | number;
        is_not_pay: boolean;
        hotel: number;
      }
    | undefined;

  setPayment(payment: typeof this.payment) {
    this.payment = payment;
  }

  get bookingParams() {
    const { range, promocode, rateMode, guestRooms } = this.appliedSearchParams || {};
    const { from, to } = range || {};

    const { room, rate, payment } = this;
    if (!from || !to || !room || !rate || !rateMode || !payment || !guestRooms) {
      return null;
    }

    return {
      from,
      to,
      room,
      rate,
      promocode,
      rateMode,
      guestRooms,
      payment,
    };
  }

  async submitReservation({ comment, ...data }: ReservationParams) {
    const getNotes = (comment: string) => {
      const id = this.root.configStore.commentNoteId;
      const code = this.root.configStore.commentNoteCode;

      if (!comment || !id || !code) {
        return [];
      }

      return [{ id, code, text: comment }];
    };

    const hotelId = this.root.configStore.hotelId;

    const notes = getNotes(comment);

    const user = { ...data };

    if (!hotelId || !this.bookingParams) {
      alert('параметры бронирования не найдены');
      return;
    }

    const { from, to, room, rate, promocode, rateMode, guestRooms, payment } = this.bookingParams;

    const services = this.serviceStore.selectedServices;

    const arrival = format(from, 'yyyy-MM-dd');
    const departure = format(to, 'yyyy-MM-dd');

    const response = await submitBooking({
      hotel_id: hotelId,
      guest: user,
      arrival,
      departure,
      room: room,
      rate: rate,
      promo_code: promocode || '',
      rate_flow: rateMode,
      rooms: guestRooms?.flatMap(room => room.params),
      payment: payment,
      services,
      notes,
    });

    return response;
  }

  async fetchServices() {
    const hotelId = this.root.configStore.hotelId;

    if (!hotelId) {
      // TODO
      throw new Error('hotelId not found');
    }

    if (!this.range?.from || !this.range?.to) {
      return;
    }

    const bodyRequest = {
      arrival: format(this.range.from, 'yyyy-MM-dd'),
      departure: format(this.range.to, 'yyyy-MM-dd'),
      promo_code: this.promocode || '',
      rooms: this.guestRooms.map(x => ({
        ...x.params,
        childs: x.params.childs.map(c => ({ ...c, code: 'CODE' })),
      })),
      rate_flow: this.rateMode || ('OPEN' as RateMode),
    };

    this.serviceStore.fetchServices(hotelId, bodyRequest);
  }
}

export class ListWithCategories<T extends { id: number; category: string }> {
  constructor(private readonly allLabel: string) {
    this.category = allLabel;

    makeAutoObservable(this);
  }

  category: string | null;
  setCategory = (category: string | null) => {
    this.category = category;
  };

  items: T[] = [];
  setItems(items: T[]) {
    this.items = items;
    const categories = this.categories;
    if (this.category && !categories.includes(this.category)) {
      this.category = this.allLabel;
    }
  }

  private hoistSelected(items: T[], selectedId?: number) {
    if (!selectedId) {
      return items;
    }
    const idx = items.findIndex(x => x.id == selectedId);
    if (idx === -1 || idx === 0) {
      return items;
    }

    const item = items[idx];
    return [item, ...items.slice(0, idx), ...items.slice(idx + 1)];
  }

  getListByCategory(selectedId?: number) {
    if (!this.category || this.category === this.allLabel) {
      return this.hoistSelected(this.items, selectedId);
    }

    const filtered = this.items.filter(item => item.category === this.category);

    return this.hoistSelected(filtered, selectedId);
  }

  pending = false;

  setPending(state: boolean) {
    this.pending = state;
  }

  get categories() {
    const categories = [...new Set(this.items.map(item => item.category).filter(x => x))];
    if (!categories.length) {
      return [];
    }

    return [this.allLabel, ...categories];
  }

  getItem(serviceId: number) {
    return this.items.find(x => x.id === serviceId);
  }
}
